import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import * as authService from '@/services/authService';
import { WebSocketClient } from '@/services/WebSocketClient';
import { useToast } from './ToastContext';

interface BalanceChangedMessage {
  type: 'BALANCE_CHANGED';
  userId?: number;
  balance?: string | number;
  version?: number | string | null;
}

interface AuthContextType {
  token: string | null;
  userInfo: API.UserInfo | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (loginData: authService.LoginForm) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserInfo: () => Promise<API.UserInfo>;
  syncUserInfoSilently: () => Promise<API.UserInfo | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const toNumberOrNull = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [token, setToken] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<API.UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const userIdRef = useRef<number | null>(null);
  const walletVersionRef = useRef(0);
  const silentSyncRef = useRef<Promise<API.UserInfo | null> | null>(null);
  const lastForegroundSyncRef = useRef(0);

  const applyUserInfo = useCallback((info: API.UserInfo): API.UserInfo => {
    const incomingUserId = toNumberOrNull(info.id);
    const incomingVersion = toNumberOrNull(info.walletVersion);
    const currentUserId = userIdRef.current;
    const currentVersion = walletVersionRef.current;
    const isSameCurrentUser = incomingUserId !== null && currentUserId !== null && incomingUserId === currentUserId;
    const shouldKeepCurrentWallet = isSameCurrentUser
      && incomingVersion !== null
      && incomingVersion < currentVersion;

    userIdRef.current = incomingUserId;
    if (!shouldKeepCurrentWallet) {
      walletVersionRef.current = incomingVersion ?? 0;
    }

    setUserInfo((prev) => {
      const isSameUser = prev && incomingUserId !== null && Number(prev.id) === incomingUserId;
      if (isSameUser && shouldKeepCurrentWallet) {
        return {
          ...info,
          balance: prev.balance,
          frozenBalance: prev.frozenBalance,
          totalAsset: prev.totalAsset,
          walletVersion: prev.walletVersion,
        };
      }

      return info;
    });
    return info;
  }, []);

  // 获取用户信息 - 使用 useCallback 保持引用稳定
  const fetchUserInfo = useCallback(async (): Promise<API.UserInfo> => {
    try {
      const info = await authService.getUserInfo();
      return applyUserInfo(info);
    } catch (error: any) {
      console.error('获取用户信息失败:', error);
      toastRef.current.error(error.message || '获取用户信息失败');
      throw error;
    }
  }, [applyUserInfo]);

  const syncUserInfoSilently = useCallback(async (): Promise<API.UserInfo | null> => {
    if (typeof window === 'undefined') {
      return null;
    }

    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      return null;
    }

    if (silentSyncRef.current) {
      return silentSyncRef.current;
    }

    setToken(currentToken);
    const syncPromise = authService.getUserInfo()
      .then((info) => applyUserInfo(info))
      .catch((error) => {
        console.debug('[Auth] 静默同步用户信息失败:', error?.message || error);
        return null;
      })
      .finally(() => {
        silentSyncRef.current = null;
      });

    silentSyncRef.current = syncPromise;
    return syncPromise;
  }, [applyUserInfo]);

  const updateUserBalance = useCallback((message: BalanceChangedMessage) => {
    const incomingVersion = toNumberOrNull(message.version);
    const incomingBalance = toNumberOrNull(message.balance);
    const incomingUserId = toNumberOrNull(message.userId);

    if (incomingUserId !== null && userIdRef.current !== null && incomingUserId !== userIdRef.current) {
      return;
    }
    if (incomingVersion === null || incomingBalance === null) {
      syncUserInfoSilently();
      return;
    }
    if (incomingVersion <= walletVersionRef.current) {
      return;
    }

    walletVersionRef.current = incomingVersion;

    setUserInfo((prev) => {
      if (!prev) {
        return prev;
      }
      if (incomingUserId !== null && incomingUserId !== Number(prev.id)) {
        return prev;
      }

      const frozenBalance = Number(prev.frozenBalance ?? 0);
      return {
        ...prev,
        balance: incomingBalance,
        totalAsset: incomingBalance + frozenBalance,
        walletVersion: incomingVersion,
      };
    });
  }, [syncUserInfoSilently]);

  // 初始化时从 localStorage 读取 token 并获取用户信息
  useEffect(() => {
    // SSR 环境跳过
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    const initAuth = async () => {
      try {
        // 检查 URL 中的模拟登录 token
        const params = new URLSearchParams(window.location.search);
        const impersonateToken = params.get('impersonate_token');
        if (impersonateToken) {
          localStorage.setItem('token', impersonateToken);
          params.delete('impersonate_token');
          const newSearch = params.toString();
          const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
          window.history.replaceState({}, '', newUrl);
          setToken(impersonateToken);
          await fetchUserInfo();
          return;
        }

        const savedToken = localStorage.getItem('token');
        if (savedToken) {
          setToken(savedToken);
          // 尝试获取用户信息
          await fetchUserInfo();
        }
      } catch (error) {
        console.error('初始化认证失败:', error);
        // 如果获取用户信息失败，清除 token
        localStorage.removeItem('token');
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [fetchUserInfo]);

  // 登录
  const login = useCallback(async (loginData: authService.LoginForm) => {
    try {
      // apiRequest 已经自动解包 Result，直接返回 LoginResponse
      const response = await authService.login(loginData);
      const { token: newToken } = response;

      // 登录成功
      // 保存 token
      localStorage.setItem('token', newToken);
      setToken(newToken);

      // 获取用户信息
      await fetchUserInfo();
    } catch (error: any) {
      console.error('登录失败:', error);
      // 待审核状态不显示 toast，让调用方处理
      if (error.code !== 'UserPendingReview') {
        toastRef.current.error(error.message || '登录失败，请稍后重试');
      }
      throw error;
    }
  }, [fetchUserInfo]);

  // 登出
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error: any) {
      console.error('登出失败:', error);
      toastRef.current.error(error.message || '登出失败');
    } finally {
      // 无论是否成功，都清除本地状态
      localStorage.removeItem('token');
      setToken(null);
      setUserInfo(null);
      userIdRef.current = null;
      walletVersionRef.current = 0;
    }
  }, []);

  // 刷新用户信息 - 使用 useCallback 保持引用稳定
  const refreshUserInfo = useCallback(async (): Promise<API.UserInfo> => {
    // 从 localStorage 读取最新的 token，而不是依赖闭包中的状态
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      throw new Error('未登录');
    }
    setToken(currentToken);
    return await fetchUserInfo();
  }, [fetchUserInfo]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (!token || !userInfo?.id) {
      return;
    }

    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${protocol}//${window.location.host}/ws/user?token=${encodeURIComponent(currentToken)}`;
    const client = new WebSocketClient({
      url,
      maxRetries: Infinity,
      baseDelay: 5000,
      maxDelay: 5000,
      silent: true,
      reconnectOnError: true,
      onStatusChange: (status) => {
        if (status === 'connected') {
          syncUserInfoSilently();
        }
      },
      onMessage: (data) => {
        const message = data as Partial<BalanceChangedMessage>;
        if (message && typeof message === 'object' && message.type === 'BALANCE_CHANGED') {
          updateUserBalance(message as BalanceChangedMessage);
        }
      },
    });

    client.connect();
    return () => {
      client.disconnect();
    };
  }, [token, userInfo?.id, syncUserInfoSilently, updateUserBalance]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncOnForeground = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      if (!localStorage.getItem('token')) {
        return;
      }
      const now = Date.now();
      if (now - lastForegroundSyncRef.current < 5000) {
        return;
      }
      lastForegroundSyncRef.current = now;
      syncUserInfoSilently();
    };

    document.addEventListener('visibilitychange', syncOnForeground);
    window.addEventListener('focus', syncOnForeground);
    return () => {
      document.removeEventListener('visibilitychange', syncOnForeground);
      window.removeEventListener('focus', syncOnForeground);
    };
  }, [syncUserInfoSilently]);

  const value: AuthContextType = {
    token,
    userInfo,
    loading,
    isAuthenticated: !!token && !!userInfo,
    login,
    logout,
    refreshUserInfo,
    syncUserInfoSilently,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * 使用认证上下文的 Hook
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
